import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { formatUnits } from 'viem';

// deep import (not the feature barrel) to avoid a wallet<->custodial import cycle.
import { useCustodialPanelState } from '@/features/custodial/hooks/useCustodialPanelState';
import { USDC_DECIMALS } from '@/lib/api/speed';
import type { BalanceResponse, SpeedBackend } from '@/lib/api/speed';
import { describeErrorText, isApiError } from '@/lib/errors';
import { queryKeys } from '@/lib/query';

import {
    readOnchainBalance,
    shouldFallBackToFullBalance,
} from '../services/onchainBalance';

import { useSpeedBackend } from './useSpeedBackend';

const BALANCE_STALE_TIME_MS = 10_000;
const BALANCE_REFETCH_INTERVAL_MS = 20_000;

interface UseSpeedBalanceOptions {
    /** Disable the query (default: enabled whenever authenticated). */
    enabled?: boolean;
    /** Override the background refetch interval (ms). */
    refetchInterval?: number;
}

interface SpeedBalanceResult {
    /** Tradeable USDC = custodial + adapter + hypercore-withdrawable (numeric). */
    numericTotal: number | null;
    /** Same total as a base-unit string, or null. */
    rawTotal: string | null;
    /**
     * USDC the withdraw saga can actually move (numeric), exactly as the server reports it.
     * `null` when the server did not state one; never derived from the other buckets here.
     */
    numericWithdrawable: number | null;
    /** Floor (USD) for the user's next withdrawal; null when the backend does not report it. */
    minWithdrawUsd: number | null;
    /** Estimated flat cost (USD) of the next withdrawal; null when the backend does not report it. */
    flatFeeUsd: number | null;
    custodialUsdc: number | null;
    adapterUsdc: number | null;
    hyperCoreWithdrawable: number | null;
    bootstrapComplete: boolean;
    custodialWallet: string | null;
    adapter: string | null;
    isLoading: boolean;
    isError: boolean;
    errorCode: string | null;
    errorMessage: string | null;
    isCustodialWalletMissing: boolean;
    refetch: () => void;
}

function toNumber(raw: string | undefined): number | null {
    if (raw === undefined) {
        return null;
    }
    try {
        return Number(formatUnits(BigInt(raw), USDC_DECIMALS));
    } catch {
        return null;
    }
}

// minWithdrawUsd/flatFeeUsd are plain decimal USD strings, not base units.
function toUsdNumber(raw: string | undefined): number | null {
    if (raw === undefined) {
        return null;
    }
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Reads the balance from public infrastructure first — `GET /speed/balance/light` for the
 * parts the chain cannot report, then one HyperEVM multicall plus the public Hyperliquid
 * info API for the actual amounts. That keeps the RPC and Hyperliquid work off our backend,
 * which otherwise repeats it for every connected client.
 *
 * Any failure logs and falls back to `GET /speed/balance`, which stays the authority and is
 * unchanged. Errors from the fallback are the ones surfaced to the UI.
 *
 * Display only. A client-derived balance is attacker-influenced, so nothing that MOVES money
 * may be gated on it — the withdraw saga validates server-side regardless of what this says.
 *
 * V1 only: the on-chain read is of the custodial wallet's buckets.
 */
async function fetchBalanceViaChain(
    backend: SpeedBackend,
    signal?: AbortSignal,
): Promise<BalanceResponse> {
    let light;
    try {
        light = await backend.fetchBalanceLight(signal);
    } catch (error) {
        if (
            !shouldFallBackToFullBalance(
                isApiError(error) ? error.status : null,
            )
        ) {
            throw error;
        }
        console.warn(
            '[Balance] balance/light unavailable; falling back to GET /speed/balance',
            error,
        );
        return backend.fetchBalance(signal);
    }

    try {
        return await readOnchainBalance(light, signal);
    } catch (error) {
        // never swallow this silently: if the on-chain path is broken for everyone, the only
        // symptom would be the backend load we set out to remove quietly coming back.
        console.warn(
            '[Balance] on-chain read failed; falling back to GET /speed/balance',
            error,
        );
        return backend.fetchBalance(signal);
    }
}

export function useSpeedBalance(
    options?: UseSpeedBalanceOptions,
): SpeedBalanceResult {
    // balance read moved to factions.backend's speed facet (V1).
    const backend = useSpeedBackend();
    // pause background polling while the Quick Trade Wallet modal is open.
    const custodialPanelOpen = useCustodialPanelState((s) => s.isOpen);

    const query = useQuery({
        queryKey: queryKeys.speed.balance(),
        queryFn: async ({ signal }) =>
            backend!.kind === 'v1'
                ? fetchBalanceViaChain(backend!, signal)
                : backend!.fetchBalance(signal),
        enabled: !!backend && (options?.enabled ?? true),
        staleTime: BALANCE_STALE_TIME_MS,
        refetchInterval: custodialPanelOpen
            ? false
            : (options?.refetchInterval ?? BALANCE_REFETCH_INTERVAL_MS),
    });

    // Must be referentially stable. Consumers thread this into useCallback and
    // useEffect dependency arrays (useCustodialBalance -> useOnboardingFlow's
    // refreshWalletReads), so a fresh closure per render re-runs those effects
    // on every render. That is what turned the onboarding `completed` step into
    // an unbounded GET /v2/auth/me loop: the refresh re-rendered the tree, which
    // rebuilt this closure, which re-ran the effect that issued the refresh.
    // (destructured so the dep is a plain identifier: depending on `query`
    // itself would be a new object every render, i.e. the very instability
    // this memo exists to remove.)
    const { refetch: runRefetch } = query;
    const refetch = useCallback(() => void runRefetch(), [runRefetch]);

    const derived = useMemo(() => {
        const data = query.data;
        if (!data) {
            return {
                numericTotal: null,
                rawTotal: null,
                numericWithdrawable: null,
                minWithdrawUsd: null,
                flatFeeUsd: null,
                custodialUsdc: null,
                adapterUsdc: null,
                hyperCoreWithdrawable: null,
                bootstrapComplete: false,
                custodialWallet: null,
                adapter: null,
            };
        }
        return {
            numericTotal: toNumber(data.totalUsdc),
            rawTotal: data.totalUsdc,
            // NEVER falls back to the total. The withdrawable max is the server's answer, and
            // the total is not a safe stand-in for it: it counts HyperCore dust below the
            // withdraw floor and the exit-sweep gas buffer, neither of which any route can
            // move. Offering the total as a max is what let the panel advertise $17.13 on
            // 2026-08-23 for a sweep MoonHub.API then capped and HyperCore refused.
            numericWithdrawable: toNumber(data.withdrawableUsdc),
            minWithdrawUsd: toUsdNumber(data.minWithdrawUsd),
            flatFeeUsd: toUsdNumber(data.flatFeeUsd),
            custodialUsdc: toNumber(data.custodialUsdc),
            adapterUsdc: toNumber(data.adapterUsdc),
            hyperCoreWithdrawable: toNumber(data.hyperCoreWithdrawable),
            bootstrapComplete: data.bootstrapComplete ?? false,
            custodialWallet: data.custodialWallet,
            adapter: data.adapter,
        };
    }, [query.data]);

    return {
        ...derived,
        isLoading: query.isLoading,
        isError: query.isError,
        errorCode: isApiError(query.error)
            ? (query.error.body?.code ?? null)
            : null,
        errorMessage: query.error
            ? describeErrorText(query.error, { operation: 'Balance' })
            : null,
        isCustodialWalletMissing:
            isApiError(query.error) &&
            query.error.body?.code === 'custodialWalletMissing',
        refetch,
    };
}
